import { useSyncExternalStore } from 'react';
import type { Template } from './layout-model';

/**
 * Option montrée dans l'aperçu (retour de l'utilisateur du 2026-10-24) : choisir « Daim » dans la liste des choix
 * de l'inspecteur le sélectionne aussi dans le rendu du canevas. Un seul éditeur est ouvert à la fois : l'état vit
 * ici, partagé par l'inspecteur qui l'écrit et le canevas qui le lit, sans traverser la coquille des studios.
 */
export interface PreviewFocus {
  fieldId: string;
  value: string;
}

let focus: PreviewFocus | null = null;
const listeners = new Set<() => void>();

export function setPreviewFocus(next: PreviewFocus | null): void {
  if (focus?.fieldId === next?.fieldId && focus?.value === next?.value) return;
  focus = next;
  for (const listener of listeners) listener();
}

export function usePreviewFocus(): PreviewFocus | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => focus,
  );
}

type Answers = Record<string, unknown>;

/**
 * Réponses de l'aperçu avec l'option choisie : posée sur le champ, et dans chaque ligne quand le champ est celui
 * d'une série (la ligne montrée au détail la porte, quelle qu'elle soit). Un booléen reçoit `true` ou `false`.
 */
export function withPreviewFocus(template: Template, answers: Answers, chosen: PreviewFocus | null): Answers {
  if (chosen === null) return answers;
  for (const section of template.sections) {
    const entry = section.fields.find((candidate) => candidate.field.field_id === chosen.fieldId);
    if (entry === undefined) continue;
    const value = entry.field.type === 'boolean' ? chosen.value === 'true' : chosen.value;
    if (section.series !== undefined) {
      const rows = answers[section.section_id];
      return Array.isArray(rows) ? { ...answers, [section.section_id]: rows.map((row) => ({ ...(row as Answers), [chosen.fieldId]: value })) } : answers;
    }
    if (section.repeat !== undefined) return answers;
    return { ...answers, [chosen.fieldId]: value };
  }
  return answers;
}
